import { useEffect, useRef } from "react";

/**
 * Keep the screen awake while `active` is true, using the Screen Wake Lock API.
 * Silently no-ops on browsers that don't support it. Re-acquires the lock when
 * the tab becomes visible again (browsers auto-release on visibility change).
 */
export default function useWakeLock(active) {
  const sentinelRef = useRef(null);

  useEffect(() => {
    let cancelled = false;

    const request = async () => {
      if (!("wakeLock" in navigator)) return;
      try {
        const s = await navigator.wakeLock.request("screen");
        if (cancelled) {
          try { await s.release(); } catch {}
          return;
        }
        sentinelRef.current = s;
        s.addEventListener("release", () => {
          if (sentinelRef.current === s) sentinelRef.current = null;
        });
      } catch {
        // ignore — user gesture may be required, or feature unavailable
      }
    };

    const release = async () => {
      const s = sentinelRef.current;
      sentinelRef.current = null;
      if (s) {
        try { await s.release(); } catch {}
      }
    };

    const onVisibility = () => {
      if (document.visibilityState === "visible" && active && !sentinelRef.current) {
        request();
      }
    };

    if (active) {
      request();
      document.addEventListener("visibilitychange", onVisibility);
    }

    return () => {
      cancelled = true;
      document.removeEventListener("visibilitychange", onVisibility);
      release();
    };
  }, [active]);
}
